<template>
    <div class="bg-nomad-900 rounded-xl p-6">
        <div class="flex justify-between items-center mb-4">
            <h2 class="text-lg font-semibold text-nomad-200">Chapters</h2>
            <button type="button" @click="addChapter"
                class="bg-nomad-accent hover:bg-nomad-accent/80 text-nomad-900 font-semibold rounded-md py-2 px-4 transition-colors">
                Add Chapter
            </button>
        </div>

        <div v-if="chapters.length === 0" class="text-center py-8 text-nomad-400">
            No chapters yet. Click "Add Chapter" to get started.
        </div>

        <div v-else class="space-y-3">
            <div v-for="(chapter, index) in chapters" :key="chapter.id || index" class="bg-nomad-800 rounded-lg p-4">
                <!-- Row 1: Chapter Number and Title -->
                <div class="flex gap-3 mb-3">
                    <!-- Chapter Number -->
                    <div class="w-20">
                        <label class="block text-xs font-medium text-nomad-400 mb-1">#</label>
                        <input type="number" v-model="chapter.number"
                            class="w-full rounded-md bg-nomad-700 border-nomad-600 text-white text-center focus:border-nomad-accent focus:ring-nomad-accent"
                            placeholder="1" min="1" required />
                    </div>

                    <!-- Title -->
                    <div class="flex-1">
                        <label class="block text-xs font-medium text-nomad-400 mb-1">Title</label>
                        <input type="text" v-model="chapter.title"
                            class="w-full rounded-md bg-nomad-700 border-nomad-600 text-white focus:border-nomad-accent focus:ring-nomad-accent"
                            placeholder="Chapter title (optional)" />
                    </div>
                </div>

                <!-- Row 2: Audio Upload and Actions -->
                <div class="flex flex-col sm:flex-row gap-3 sm:items-end">
                    <!-- Audio Upload -->
                    <div class="flex-1">
                        <label class="block text-xs font-medium text-nomad-400 mb-1">
                            Audio <span class="text-red-400">*</span>
                        </label>
                        <AudioUploader v-model="chapter.audio" />
                    </div>

                    <!-- Actions -->
                    <div class="flex items-center gap-2 flex-wrap">
                        <label class="flex items-center cursor-pointer">
                            <input type="checkbox" v-model="chapter.is_preview"
                                class="rounded bg-nomad-700 border-nomad-600 text-nomad-accent focus:ring-nomad-accent" />
                            <span class="ml-2 text-nomad-300 text-sm whitespace-nowrap">Preview</span>
                        </label>

                        <button v-if="chapter.id" type="button" @click="saveChapter(chapter)"
                            class="bg-blue-600 hover:bg-blue-700 text-white rounded-md py-2 px-4 transition-colors text-sm whitespace-nowrap"
                            :disabled="saving">
                            Save
                        </button>

                        <button type="button" @click="removeChapter(index)"
                            class="bg-red-600 hover:bg-red-700 text-white rounded-md py-2 px-4 transition-colors">
                            <span class="icon-x"></span>
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="unsavedChapters.length > 0" class="mt-4 flex justify-end">
            <button type="button" @click="saveAllChapters"
                class="bg-nomad-accent hover:bg-nomad-accent/80 text-nomad-900 font-semibold rounded-md py-3 px-6 transition-colors"
                :disabled="saving">
                <span v-if="saving">Saving...</span>
                <span v-else>Save All Chapters</span>
            </button>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import AudioUploader from '@/Pages/Backstage/Components/AudioUploader.vue';
import axios from 'axios';
import { toast } from 'vue3-toastify';

const props = defineProps({
    audiobookId: {
        type: Number,
        required: true
    }
});

const chapters = ref([]);
const saving = ref(false);

const unsavedChapters = computed(() => {
    return chapters.value.filter(ch => !ch.id);
});

onMounted(() => {
    loadChapters();
});

function loadChapters() {
    axios.get(`/api/backstage/audiobooks/${props.audiobookId}/chapters`)
        .then((response) => {
            // Map audio_path to audio for the component
            chapters.value = response.data.map(chapter => ({
                ...chapter,
                audio: chapter.audio_path
            }));
        })
        .catch((error) => {
            console.error('Error loading chapters:', error);
            toast.error('Failed to load chapters');
        });
}

function addChapter() {
    const nextNumber = chapters.value.length > 0
        ? Math.max(...chapters.value.map(ch => ch.number)) + 1
        : 1;

    chapters.value.push({
        number: nextNumber,
        title: null,
        audio: null,
        is_preview: false,
        is_published: true,
    });
}

function saveChapter(chapter) {
    if (!chapter.audio || !chapter.number) {
        toast.error('Chapter number and audio are required');
        return;
    }

    saving.value = true;

    const request = chapter.id
        ? axios.put(`/api/backstage/audiobooks/chapters/${chapter.id}`, {
            number: chapter.number,
            title: chapter.title,
            audio: chapter.audio,
            is_preview: chapter.is_preview,
            is_published: chapter.is_published,
        })
        : axios.post('/api/backstage/audiobooks/chapters', {
            audiobook_id: props.audiobookId,
            number: chapter.number,
            title: chapter.title,
            audio: chapter.audio,
            is_preview: chapter.is_preview,
            is_published: chapter.is_published,
        });

    request
        .then((response) => {
            // Map audio_path to audio when response comes back
            Object.assign(chapter, { ...response.data, audio: response.data.audio_path });
            toast.success('Chapter saved successfully');
        })
        .catch((error) => {
            console.error('Error saving chapter:', error);
            toast.error('Error saving chapter');
        })
        .finally(() => {
            saving.value = false;
        });
}

function saveAllChapters() {
    const chaptersToSave = unsavedChapters.value.filter(ch => ch.audio && ch.number);

    if (chaptersToSave.length === 0) {
        toast.error('No valid chapters to save');
        return;
    }

    saving.value = true;

    const requests = chaptersToSave.map(chapter =>
        axios.post('/api/backstage/audiobooks/chapters', {
            audiobook_id: props.audiobookId,
            number: chapter.number,
            title: chapter.title,
            audio: chapter.audio,
            is_preview: chapter.is_preview,
            is_published: chapter.is_published,
        })
    );

    Promise.all(requests)
        .then((responses) => {
            responses.forEach((response, index) => {
                Object.assign(chaptersToSave[index], {
                    ...response.data,
                    audio: response.data.audio_path
                });
            });
            toast.success('All chapters saved successfully');
        })
        .catch((error) => {
            console.error('Error saving chapters:', error);
            toast.error('Error saving chapters');
        })
        .finally(() => {
            saving.value = false;
        });
}

function removeChapter(index) {
    const chapter = chapters.value[index];

    if (chapter.id) {
        if (!confirm('Are you sure you want to delete this chapter?')) {
            return;
        }

        axios.delete(`/api/backstage/audiobooks/chapters/${chapter.id}`)
            .then(() => {
                chapters.value.splice(index, 1);
                toast.success('Chapter deleted successfully');
            })
            .catch((error) => {
                console.error('Error deleting chapter:', error);
                toast.error('Error deleting chapter');
            });
    } else {
        chapters.value.splice(index, 1);
    }
}
</script>